<script>
import {useRoute, useRouter} from "vue-router";
import {usePossibleDuplicateStore} from "@/javascript/stores/possibleDuplicate";
import {useUserStore} from "@/javascript/stores/user";
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";
import Icon from "@/javascript/views/components/Icon.vue";
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import BaseModal from "@/javascript/views/components/BaseModal.vue";
import {useOrganizationStore} from "@/javascript/stores/organizations";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";

export default {
  name: "DuplicateDonorReview",
  components: {Icon, LoadingSpinner, BaseModal},
  setup() {
    const route = useRoute();
    const router = useRouter();
    const possibleDuplicateStore = usePossibleDuplicateStore();
    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();
    return {route, router, possibleDuplicateStore, userStore, organizationStore, analyticEventStore, ApplicationHelper};
  },
  data() {
    return {
      index: 0,
      survivingId: null,
      confirmingKeepSeparate: false,
      confirming: false,
      submitting: false,
      errorMessage: ""
    };
  },
  computed: {
    loading() {
      return this.possibleDuplicateStore.loadingReview;
    },
    primary() {
      return this.possibleDuplicateStore.currentPrimary;
    },
    matches() {
      return this.possibleDuplicateStore.currentMatches;
    },
    primaryId() {
      return this.route.params.id;
    },
    currentMatch() {
      return this.matches[this.index] || null;
    },
    duplicate() {
      return this.currentMatch?.duplicate_donor || null;
    },
    total() {
      return this.matches.length;
    },
    left() {
      return this.primary;
    },
    right() {
      return this.duplicate;
    },
    // Recommended survivor = the OLDER record (earlier member_since).
    recommendedId() {
      if (!this.left || !this.right) return null;
      const l = new Date(this.left.member_since).getTime();
      const r = new Date(this.right.member_since).getTime();
      return r < l ? this.right.id : this.left.id;
    },
    survivor() {
      return [this.left, this.right].find((d) => d && d.id === this.survivingId) || null;
    },
    duplicateToArchive() {
      return [this.left, this.right].find((d) => d && d.id !== this.survivingId) || null;
    }
  },
  watch: {
    primaryId(id) {
      if (id) {
        this.ensureService();
        this.possibleDuplicateStore.loadReview(id);
      }
    },
    // When the pager moves to a new candidate, reset the survivor to the recommended default.
    currentMatch: {
      immediate: true,
        handler() {
        this.survivingId = this.recommendedId;
        this.errorMessage = "";
      }
    }
  },
  mounted() {
    this.recordAnalyticEvent("duplicate-donor-review-open");
    if (this.primaryId) {
      this.ensureService();
      this.possibleDuplicateStore.loadReview(this.primaryId);
    }
  },
  methods: {
    recordAnalyticEvent(action, metadata = {}) {
      const orgId = this.organizationStore.organizationId;
      if (!orgId) return;
      this.analyticEventStore.setOrganizationId(orgId);
      this.analyticEventStore.logEvent({action, metadata});
    },
    backToList() {
      const routeName = this.userStore.userScopedToSubdomain ? "client-dashboard-scoped" :
        "client-dashboard";
      const params = {...this.route.params, panel: "duplicate-donors"};
      delete params.id;
      this.router.push({name: routeName, params});
    },
    ensureService() {
      if (!this.possibleDuplicateStore.service && this.organizationStore.organizationId)
      {
        this.possibleDuplicateStore.setOrganizationId(this.organizationStore.organizationId);
      }
    },
    initials(donor) {
      const f = donor?.first_name?.[0] || "";
      const l = donor?.last_name?.[0] || "";
      return (f + l).toUpperCase() || "?";
    },
    householdRoute(household) {
      const routeName = this.userStore.userScopedToSubdomain ? "client-dashboard-scoped" :
        "client-dashboard";
      const params = this.userStore.userScopedToSubdomain
        ? {panel: "households", id: household.id}
        : {org: this.userStore.currentOrganization?.slug, panel: "households", id: household.id};
      return {name: routeName, params, query: {returnToReview: this.primaryId}};
    },
    money(cents) {
      return ApplicationHelper.formatMoney(cents);
    },
    gift(donor) {
      return donor?.last_gift_at ? new Date(donor.last_gift_at).toLocaleDateString() : "—";
    },
    since(donor) {
      return donor?.member_since ? new Date(donor.member_since).toLocaleDateString() : "—";
    },
    dob(donor) {
      if (!donor?.date_of_birth) return "—";
      // date_of_birth is a date-only value (YYYY-MM-DD). Parse the components as a
      // LOCAL date so it isn't shifted a day by UTC parsing. (kiro review / DONATE-1646)
      const [year, month, day] = String(donor.date_of_birth).slice(0,
        10).split("-").map(Number);
      if (!year || !month || !day) return "—";
      return new Date(year, month - 1, day).toLocaleDateString();
    },
    differs(field) {
      if (field === "household") {
        return this.left?.household?.id !== this.right?.household?.id;
      }
      return this.left?.[field] !== this.right?.[field];    },
    isRecommended(id) {
      return id === this.recommendedId;
    },
    prev() {
      if (this.index > 0) this.index -= 1;
    },
    next() {
      if (this.index < this.total - 1) this.index += 1;
    },
    openConfirm() {
      if (!this.survivingId) return;
      this.errorMessage = "";
      this.confirming = true;
    },
    cancelConfirm() {
      this.confirming = false;
    },
    openKeepSeparateConfirm() {
      this.errorMessage = "";
      this.confirmingKeepSeparate = true;
    },
    cancelKeepSeparateConfirm() {
      this.confirmingKeepSeparate = false;
    },
    async keepSeparate() {
      if (this.submitting || !this.currentMatch) return;
      this.submitting = true;
      this.errorMessage = "";
      const keptFlagId = this.currentMatch.id;
      try {
        const res = await this.possibleDuplicateStore.keepSeparate(keptFlagId);
        if (res && res.status === "kept_separate") {
          try {
            await this.possibleDuplicateStore.loadReview(this.primaryId);
          } catch (e) {
            this.errorMessage = "Failed to reload review data. Please refresh.";
            return;
          }
          this.confirmingKeepSeparate = false;
          this.recordAnalyticEvent("duplicate-donor-keep-separate", {flag_id: keptFlagId});
          if (this.matches.length === 0) {
            this.backToList();
          } else if (this.index > this.total - 1) {
            this.index = Math.max(0, this.total - 1);
          }
        } else {
          this.errorMessage = "Could not keep these records separate. Please try again.";
        }
      } catch (e) {
        this.errorMessage = "Could not keep these records separate. Please try again.";
      } finally {
        this.submitting = false;
      }
    },
    async confirmMerge() {
      if (this.submitting || !this.survivor || !this.duplicateToArchive) return;
      this.submitting = true;
      this.errorMessage = "";
      const survivorId = this.survivor.id;
      const duplicateId = this.duplicateToArchive.id;
      try {
        const res = await this.possibleDuplicateStore.merge(
          this.currentMatch.id,
          survivorId,
          duplicateId
        );

        if (res?.success) {
          try {
            await this.possibleDuplicateStore.loadReview(this.primaryId);
          } catch (e) {
            this.errorMessage = "Failed to reload review data. Please refresh.";
            return;
          }
          this.confirming = false;
          this.recordAnalyticEvent("duplicate-donor-merge-confirmed", {
            survivor_id: survivorId,
            duplicate_id: duplicateId
          });
          if (this.matches.length === 0) {
            this.backToList();
          } else if (this.index > this.total - 1) {
            this.index = Math.max(0, this.total - 1);
          }
        } else {
          this.errorMessage = res?.error || "The merge could not be completed.";
        }
      } catch (e) {
        this.errorMessage = "The merge could not be completed.";
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<template>
  <div class="duplicate-donor-review p-4 pt-2 bg-white text-sm">
    <!-- Header: back link (left) + pager (right) -->
    <div class="flex items-center justify-between mb-4">
      <button
        class="flex items-center gap-1 text-sm text-gray-600"
        @click="backToList"
      >
        <Icon
          icon="chevron-left"
          size="14px"
        />
        Back to Duplicate Donors
      </button>

      <div
        v-if="primary && total > 1"
        class="flex items-center gap-2 text-sm"
      >
        <span class="text-xs text-gray-500">{{ index + 1 }} of {{ total }}</span>
        <button
          class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-40"
          :disabled="index === 0"
          @click="prev"
        >
          <Icon
            icon="chevron-left"
            size="14px"
          />
        </button>
        <button
          class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-40"
          :disabled="index === total - 1"
          @click="next"
        >
          <Icon icon="chevron-right" size="14px" />
        </button>
      </div>
    </div>

    <loading-spinner v-if="loading" />

    <template v-else-if="primary && left && right">
      <h1 class="text-xl font-semibold mb-2">
        Compare Duplicate Records
      </h1>
      <p class="text-xs text-gray-500 mb-4">
        Choose which record to keep. The other record will be archived and its donations,
        subscriptions, and history will move to the record you keep. This cannot be undone.
      </p>

      <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
        <!-- Left: primary -->
        <label
          class="merge-card"
          :class="{selected: survivingId === left.id}"
        >
          <div
            class="flex items-center justify-between px-4 py-3 rounded-t-xl"
            :class="survivingId === left.id ? 'bg-green-100' : 'bg-gray-100'"
          >
            <span class="inline-flex items-center gap-2">
              <input type="radio" name="survivor" :value="left.id" :checked="survivingId === left.id" @change="survivingId = left.id">
              <span class="font-semibold text-sm">
                Keep this record<span v-if="isRecommended(left.id)"> (recommended)</span>
              </span>
            </span>
            <span
              v-if="survivingId === left.id"
              class="text-xs bg-default_primary_dark text-default_white rounded-full px-2 py-0.5"
            >
              Will be kept
            </span>
          </div>
          <div class="p-4">
            <div class="flex items-center gap-3">
              <span
                class="w-12 h-12 rounded-full flex items-center justify-center font-semibold"
                :class="survivingId === left.id ? 'bg-green-100 text-default_primary_dark' : 'bg-blue-100 text-blue-800'"
              >
              {{ initials(left) }}
              </span>
              <div class="min-w-0">
                <p class="font-semibold text-sm">{{ left.full_name || "—" }}</p>
                <p class="text-xs text-gray-500">{{ left.email || "—" }}</p>
              </div>
            </div>
            <dl class="mt-3 text-xs space-y-1">
              <div class="flex justify-between">
                <dt class="text-gray-400">Member since</dt>
                <dd>{{ since(left) }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Date of birth</dt>
                <dd>{{ dob(left) }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Household</dt>
                <dd :class="{'text-yellow-700 font-medium': differs('household')}">
                  <router-link
                    v-if="left.household"
                    :to="householdRoute(left.household)"
                    class="text-blue-600"
                  >
                    {{ left.household.name }}
                  </router-link>
                  <span v-else>—</span>
                </dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Total giving</dt>
                <dd>{{ money(left.all_time_donations) }} {{ left.currency }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Gifts</dt>
                <dd>{{ left.successful_donations_count }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Last gift</dt>
                <dd>{{ gift(left) }}</dd>
              </div>
            </dl>
          </div>
        </label>

        <!-- Right: duplicate candidate -->
        <label
          class="merge-card"
          :class="{selected: survivingId === right.id}"
        >
          <div
            class="flex items-center justify-between px-4 py-3 rounded-t-xl"
            :class="survivingId === right.id ? 'bg-green-100' : 'bg-gray-100'"
          >
            <span class="inline-flex items-center gap-2">
              <input type="radio" name="survivor" :value="right.id" :checked="survivingId === right.id" @change="survivingId = right.id">
              <span class="font-semibold text-sm">
                Keep this record<span v-if="isRecommended(right.id)"> (recommended)</span>
              </span>
            </span>
            <span
              v-if="survivingId === right.id"
              class="text-xs bg-default_primary_dark text-default_white rounded-full px-2 py-0.5"
            >
              Will be kept
            </span>
          </div>
          <div class="p-4">
            <div class="flex items-center gap-3">
              <span
                class="w-12 h-12 rounded-full flex items-center justify-center font-semibold"
                :class="survivingId === right.id ? 'bg-green-100 text-default_primary_dark' : 'bg-blue-100 text-blue-800'"
              >
              {{ initials(right) }}
              </span>
              <div class="min-w-0">
                <p class="font-semibold text-sm">{{ right.full_name || "—" }}</p>
                <p class="text-xs text-gray-500">{{ right.email || "—" }}</p>
              </div>
            </div>
            <dl class="mt-3 text-xs space-y-1">
              <div class="flex justify-between">
                <dt class="text-gray-400">Member since</dt>
                <dd>{{ since(right) }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Date of birth</dt>
                <dd>{{ dob(right) }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Household</dt>
                <dd :class="{'text-yellow-700 font-medium': differs('household')}">
                  <router-link
                    v-if="right.household"
                    :to="householdRoute(right.household)"
                    class="text-blue-600"
                  >
                    {{ right.household.name }}
                  </router-link>
                  <span v-else>—</span>
                </dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Total giving</dt>
                <dd>{{ money(right.all_time_donations) }} {{ right.currency }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Gifts</dt>
                <dd>{{ right.successful_donations_count }}</dd>
              </div>
              <div class="flex justify-between">
                <dt class="text-gray-400">Last gift</dt>
                <dd>{{ gift(right) }}</dd>
              </div>
            </dl>
          </div>
        </label>
      </div>

      <!-- What happens when you merge? + Action buttons -->
      <div class="flex items-center justify-between mt-4 gap-4">
        <div class="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
          <p class="font-semibold mb-1">
            What happens when you merge?
          </p>
          <p>
            The duplicate record will be combined with the primary donor, including their donation
            history, subscriptions, organization memberships, and household information. The duplicate
            record will be archived.
          </p>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <button
            class="btn-outline btn-sm"
            :disabled="submitting"
            @click="openKeepSeparateConfirm"
          >
            Keep Separate
          </button>
          <button
            class="btn-filled btn-sm"
            :disabled="!survivingId"
            @click="openConfirm"
          >
            <i class="fa-solid fa-merge text-white" />
            Merge Records
          </button>
        </div>
      </div>

      <Teleport to="body">
        <BaseModal
          v-if="confirming"
          title="Confirm merge"
          size="small"
          @close="cancelConfirm"
        >
          <div class="text-sm">
            <p class="mb-3">
              Merge <strong>{{ duplicateToArchive && duplicateToArchive.full_name }}</strong> ({{ duplicateToArchive.email }})
              into <strong>{{ survivor && survivor.full_name }}</strong> ({{ survivor.email }})?
            </p>
            <p class="text-xs mb-4">
              All donations, subscriptions, and history will move to
              {{ survivor && survivor.full_name }} ({{ survivor.email }}). This action cannot be undone.
            </p>
            <p
              v-if="errorMessage"
              class="text-xs text-red-500 mb-3"
            >
              {{ errorMessage }}
            </p>
            <div class="flex justify-end gap-2">
              <button
                class="btn-outline btn-sm"
                :disabled="submitting"
                @click="cancelConfirm"
              >
                Cancel
              </button>
              <button
                class="btn-filled btn-sm"
                :disabled="submitting"
                @click="confirmMerge"
              >
                {{ submitting ? "Merging…" : "Confirm merge" }}
              </button>
            </div>
          </div>
        </BaseModal>
      </Teleport>

      <Teleport to="body">
        <BaseModal
          v-if="confirmingKeepSeparate"
          title="Keep these records separate?"
          size="small"
          @close="cancelKeepSeparateConfirm"
        >
          <div class="text-sm">
            <p class="mb-3">
              Keep <strong>{{ primary && primary.full_name }}</strong> ({{ primary.email }})
              and <strong>{{ duplicate && duplicate.full_name }}</strong> ({{ duplicate.email }}) as separate
              donors?
            </p>
            <p class="text-xs text-gray-500 mb-4">
              These records will remain separate and will no longer be flagged as potential
              duplicates.
            </p>
            <p
              v-if="errorMessage"
              class="text-xs text-red-500 mb-3"
            >
              {{ errorMessage }}
            </p>
            <div class="flex justify-end gap-2">
              <button
                class="btn-outline btn-sm"
                :disabled="submitting"
                @click="cancelKeepSeparateConfirm"
              >
                Cancel
              </button>
              <button
                class="btn-filled btn-sm"
                :disabled="submitting"
                @click="keepSeparate"
              >
                {{ submitting ? "Keeping separate…" : "Keep Separate" }}
              </button>
            </div>
          </div>
        </BaseModal>
      </Teleport>
    </template>

    <div
      v-else
      class="text-center text-gray-500 py-6"
    >
      No pending duplicate matches for this donor.
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.duplicate-donor-review {
  .merge-card {
    @apply block bg-white rounded-xl border border-gray-300 cursor-pointer min-w-0;
    &.selected {
      @apply border-default_primary_dark;
    }
  }
}
</style>
